import React, { createContext, useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';

import { withCookie } from '@spotahome/ui-library';

import Metrics from '@spotahome/soyuz-metrics';

import { useSoyuzFlag, useSoyuzLocales } from '@spotahome/soyuz/client';

import isMobileScreen from '@common/utils/isMobileScreen';

import getGqlClient from '@common/graphql/client';
import { getMatcherQuery } from '@common/graphql/queries/matcher';

const LIMIT_HOMECARDS = 15;

const COOKIE_CONFIG = {
  cookieName: 'matcher-onboarding-done',
  cookieExpires: {
    days: 30
  }
};

const adaptFilters = newFilters => {
  return {
    budget: newFilters.budget,
    moveIn: newFilters['move-in'],
    moveOut: newFilters['move-out'],
    flexibleMoveIn: parseInt(newFilters.flexibleMoveIn, 10),
    flexibleMoveOut: parseInt(newFilters.flexibleMoveOut, 10),
    type: newFilters['type[]']
  };
};

const INITIAL_FILTERS = {
  'move-in': '',
  'move-out': '',
  budget: '',
  flexibleMoveIn: null,
  flexibleMoveOut: null,
  'type[]': [],
  'topology[]': []
};

export const MatcherContext = createContext();

export const MatcherProvider = withCookie(COOKIE_CONFIG)(
  ({
    showMatcherModal,
    children,
    maxPriceInCity,
    currency,
    initialUnitPrices,
    cityId,
    minimumStaying,
    acceptCookie,
    isCookieAccepted
  }) => {
    const [page, setPage] = useState(1);
    const [matcherFilters, setMatcherFilters] = useState(INITIAL_FILTERS);
    const flag = useSoyuzFlag('matcher');
    const isMatcherEnabled = isMobileScreen() && flag.isOn();

    const { current: locale } = useSoyuzLocales();

    const [isMatcherModalOpen, setIsMatcherModalOpen] = useState(
      showMatcherModal
    );

    const [isEnd, setIsEnd] = useState(false);

    useEffect(() => {
      if (isMatcherModalOpen) {
        acceptCookie();
      }
    }, [acceptCookie, isMatcherModalOpen]);

    const [homecards, setHomecards] = useState([]);

    const showMatcher = () => {
      Metrics.ga.sendEvent('Matcher', 'open matcher', 'click button');
      setIsMatcherModalOpen(true);
    };
    const hideMatcher = () => setIsMatcherModalOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const requestHomecards = async () => {
      try {
        const { data } = await getMatcherQuery(getGqlClient())(
          cityId,
          {
            ...adaptFilters(matcherFilters),
            page
          },
          locale,
          LIMIT_HOMECARDS
        );
        if (
          !data.search.homecards.length ||
          data.search.homecards.length !== LIMIT_HOMECARDS
        ) {
          setIsEnd(true);
        } else {
          setHomecards(data.search.homecards);
        }
      } catch (error) {
        console.error(error);
      }
    };

    useEffect(() => {
      if (page > 1) {
        requestHomecards();
      }
    }, [page, requestHomecards]);

    const requestNextHomecards = () => {
      setPage(page + 1);
    };

    const resetFilters = () => {
      setPage(1);
      setMatcherFilters(INITIAL_FILTERS);
    };

    return (
      <MatcherContext.Provider
        value={{
          isMatcherEnabled,
          isMatcherModalOpen: isMatcherEnabled && isMatcherModalOpen,
          showMatcher,
          setHomecards,
          homecards,
          hideMatcher,
          maxPriceInCity,
          currency,
          initialUnitPrices,
          cityId,
          minimumStaying,
          requestHomecards,
          skipOnboarding: showMatcherModal || isCookieAccepted(),
          matcherFilters,
          setMatcherFilters,
          requestNextHomecards,
          isEnd,
          resetFilters
        }}
      >
        {children}
      </MatcherContext.Provider>
    );
  }
);

MatcherProvider.propTypes = {
  showMatcherModal: PropTypes.bool.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  cityId: PropTypes.string.isRequired,
  maxPriceInCity: PropTypes.number.isRequired,
  currency: PropTypes.shape({ isoCode: PropTypes.string }),
  initialUnitPrices: PropTypes.arrayOf(PropTypes.number),
  minimumStaying: PropTypes.number.isRequired,
  isCookieAccepted: PropTypes.func.isRequired,
  acceptCookie: PropTypes.func.isRequired
};

MatcherProvider.defaultProps = {
  currency: undefined,
  initialUnitPrices: []
};

export const MatcherConsumer = MatcherContext.Consumer;

export const useMatcherContext = () => useContext(MatcherContext);

export default {
  MatcherContext,
  MatcherProvider,
  MatcherConsumer,
  useMatcherContext
};
